import { useEffect, useMemo, useState } from 'react';
import cm from 'classnames';

import { StoryBoardTracks } from 'services/storyboard.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { setRecentAction } from 'components/app/_mediator/app-logger.slice';
import { selectThumbnailData } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { getClipConfidenceScore, shouldShowPinButton } from 'components/app/_helpers/_app.helper';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { getMarkersEnabledRange } from 'components/app/_mediator/range-markers/range-markers.helper';
import { useClipPlaybackSb } from 'components/app/_mediator/clip-playback/use-clip-playback-sb';
import { setSelectedClips } from 'components/app/storyboard/_mediator/sb.slice';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { setRecentlyMovedClips } from 'components/app/editor/_mediator/editor-controls.slice';
import { useClipsNotFound } from 'components/app/editor/_mediator/editor-clips/use-clips-not-found';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { useSbDndSelect } from './sb-tracks-dnd/use-sb-dnd-select';
import { useSbDnd } from './sb-tracks-dnd/use-sb-dnd';
import { useHighlightPin } from 'components/app/selector/selector-clips/use-highlight-pin';

import { TrackCell } from 'components/app/app-components/track-container/track-elements';
import { ClipContainer } from 'components/app/app-components/track-clip/clip-container';
import { ClipElement } from 'components/app/app-components/track-clip/clip-element';
import { ClipRatioContainer } from 'components/app/app-components/track-clip/clip-ratio-container';
import { ClipMenuContainer } from 'components/app/app-components/track-clip/clip-menu';
import { ClipLinking } from 'components/app/app-components/track-clip/clip-linking';
import { ClipPoster } from 'components/app/app-components/track-clip/clip-poster';
import { ClipUi } from 'components/app/app-components/track-clip/clip-ui';
import { SbCellResize } from 'components/app/storyboard/sb-cell-resize/sb-cell-resize';
import styles from './_sb-tracks.module.scss';

interface Props {
  track: StoryBoardTracks;
  clipId: CellClip;
  width: number;
  index: number;
}

export const SbCell = ({ index, track, width, clipId }: Props) => {
  const dragger = useAppSelector((state) => state.app.dragger);
  const errorRateMap = useAppSelector((state) => state.sb.errorRateMap);
  const fileConfigs = useAppSelector((state) => state.app__files.configs);
  const selectedClips = useAppSelector((state) => state.sb.selectedClips);
  const recentlyMovedClips = useAppSelector((state) => state.editor__controls.recentlyMovedClips);
  const tsBoundaryClip = useAppSelector((state) => state.timestamp.timeBoundaryClip);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);
  const { activeMenuClip, clipsMap, activeClip, markers, configPanel, storyConfig } = useAppSelector(
    (state) => state.editor
  );
  const { sbSelectClip } = useSbCb();
  const { setHoveredClip } = useClipThumbPrevContext();
  const getSelectedClips = useSbDndSelect(clipId, index, track);
  const clipNotFoundCb = useClipsNotFound();
  const dispatch = useAppDispatch();

  const [hover, setHover] = useState(false);
  const [dnd, setDnd] = useState(false);

  const { drag, drop, onDragEnter, onDragLeave, onDndOver } = useSbDnd(track, index, clipId, () => setDnd(false));

  const clipData = useMemo(() => {
    if (!clipsMap || clipId === EMPTY_CELL) return;
    return clipsMap[clipId]!;
  }, [clipsMap, clipId]);

  const { togglePin } = useHighlightPin(clipData ?? ({} as Clip));

  const isActive = useMemo(() => {
    return !!activeClip && activeClip === clipId;
  }, [activeClip, clipId]);

  const confidenceScore = useMemo(() => getClipConfidenceScore(clipData), [clipData]);
  const showPinButton = useMemo(() => shouldShowPinButton(clipData), [clipData]);

  const isOutOfRange = useMemo(() => {
    const enabledRange = getMarkersEnabledRange(storyConfig, markers?.length ?? 0, 'clip');
    return !enabledRange.includes(index) && clipId;
  }, [storyConfig?.finishMarkerId, storyConfig?.startMarkerId, markers]);

  useClipPlaybackSb({ clipId, index, track, clipData });

  const onMouseEnter = () => {
    setHoveredClip(clipId);
    if (clipId) setHover(true);
  };

  const onMouseleave = () => {
    setHover(false);
  };

  const handleSelect = (event: React.MouseEvent) => {
    dispatch(
      setRecentAction({
        key: 'USER',
        desc: `sbCell: handleSelect`,
        payload: { clipId, shift: event.shiftKey },
      })
    );
    if (event.shiftKey) dispatch(setSelectedClips(getSelectedClips() ?? []));
    else {
      sbSelectClip(clipId, index, track);
      dispatch(setSelectedClips([]));
    }
    dispatch(setRecentlyMovedClips([]));
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clipData) togglePin();
  };

  useEffect(() => {
    if (clipId !== EMPTY_CELL && !clipData) clipNotFoundCb(clipId, { track, cell: index });
  }, [clipData]);

  return (
    <TrackCell
      className={styles.SbCell}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseleave}
      onClick={handleSelect}
      width={width}
      clipId={clipId}
    >
      {hover && <SbCellResize clip={clipData ?? null} track={track} cell={index} dnd={dnd} />}
      <ClipContainer
        ref={drop}
        modifiers={{
          active: isActive,
          boundary: !!clipId && tsBoundaryClip === clipId,
          selected: selectedClips.includes(clipId ?? ''),
          error: !!(clipId && errorRateMap[clipId]),
          menuActive: clipId === activeMenuClip,
          empty: clipId === EMPTY_CELL,
          draggedOver: onDndOver !== null,
          cannotDrop: onDndOver === false,
          outOfRange: !!isOutOfRange,
          editing:
            (isActive && dragger === EnumDragger.tsSlider) ||
            (clipId && recentlyMovedClips[clipId]) ||
            (isActive && configPanel === 'clip'),
        }}
        className={cm(`${track}_${index}`)}
        onDragLeave={clipData ? undefined : onDragLeave}
        onDragEnter={clipData ? undefined : onDragEnter}
      >
        {clipData && (
          <ClipElement
            ref={drag}
            onDragLeave={onDragLeave}
            onDragEnter={onDragEnter}
            onMouseDown={() => {
              dispatch(setRecentlyMovedClips([]));
              setDnd(true);
            }}
            onMouseUp={() => setDnd(false)}
          >
            <div className={styles.sbCell__overlap} />
            <ClipMenuContainer clipId={clipData.clipId} cell={index} track={track}>
              <ClipRatioContainer>
                <ClipUi clipData={clipData} />
                <ClipPoster
                  clipData={clipData}
                  fileConfig={fileConfigs[clipData.mediaFileId]}
                  thumbnail={selectThumbnailData(clipData.clipId, clipThumbnails)}
                />
                {clipData?.isAutoHighlight && (
                  <div className={styles.AutoBadge}>
                    <span className={styles.AutoBadge__icon}>✨</span>
                    <span className={styles.AutoBadge__text}>Auto</span>
                    {confidenceScore !== null && <span className={styles.AutoBadge__score}>{confidenceScore}%</span>}
                  </div>
                )}
                {showPinButton && (
                  <button
                    className={cm(styles.PinButton, {
                      [styles['PinButton--pinned']]: true,
                      [styles['PinButton--topRight']]: true,
                    })}
                    onClick={handlePinClick}
                    title="Unpin from storyboard"
                    aria-label="Unpin from storyboard"
                  >
                    📌
                  </button>
                )}
              </ClipRatioContainer>
              <ClipLinking clipData={clipData} />
            </ClipMenuContainer>
          </ClipElement>
        )}
      </ClipContainer>
    </TrackCell>
  );
};
